import type {
  CollectionActions,
  CollectionStateSlice,
  Reference,
} from '@freelancer/datastore/core';
import {
  mergeDocuments,
  OrderByDirection,
  pluckDocumentFromRawStoreCollectionState,
  removeDocumentById,
  transformIntoDocuments,
  updateWebsocketDocuments,
} from '@freelancer/datastore/core';
import type { PartialBy } from '@freelancer/types';
import type {
  QuotationItemCreateRequestApi,
  QuotationItemRevisionsGetResponseApi,
  QuotationItemsGetResponseApi,
} from 'api-typings/quotations/quotations';
import type { QuotationItemRevisionCreateRequest } from './quotation-item-revisions.model';
import { transformQuotationItemRevision } from './quotation-item-revisions.transformer';
import type { QuotationItemRevisionsCollection } from './quotation-item-revisions.types';

function isQuotationItemsGetResponse(
  response: QuotationItemsGetResponseApi | QuotationItemRevisionsGetResponseApi,
): response is QuotationItemsGetResponseApi {
  return (
    (response as QuotationItemRevisionsGetResponseApi).item_id === undefined
  );
}

function isQuotationItemRevisionCreateResponse(
  request:
    | PartialBy<QuotationItemCreateRequestApi, 'quotation_id'>
    | QuotationItemRevisionCreateRequest,
): request is QuotationItemRevisionCreateRequest {
  return (
    (request as QuotationItemRevisionCreateRequest).previous_item_version_id !==
    undefined
  );
}
export function quotationItemRevisionsReducer(
  state: CollectionStateSlice<QuotationItemRevisionsCollection> = {},
  action: CollectionActions<QuotationItemRevisionsCollection>,
): CollectionStateSlice<QuotationItemRevisionsCollection> {
  switch (action.type) {
    case 'API_FETCH_SUCCESS': {
      if (action.payload.type === 'quotationItemRevisions') {
        const { result, ref, order } = action.payload;
        const quotationItems = isQuotationItemsGetResponse(result)
          ? result.quotation_items
          : result.items;
        return mergeDocuments<QuotationItemRevisionsCollection>(
          state,
          transformIntoDocuments(
            quotationItems,
            transformQuotationItemRevision,
          ),
          order,
          ref,
        );
      }
      return state;
    }
    case 'API_PUSH_SUCCESS': {
      if (action.payload.type === 'quotationItemRevisions') {
        const { result, ref, rawRequest } = action.payload;
        // On item revision creation, we want to pluck the now outdated 'previous' item version
        // before  deleting it from the collection state and replacing it with the new one.
        const previousQuotationItem = isQuotationItemRevisionCreateResponse(
          rawRequest,
        )
          ? pluckDocumentFromRawStoreCollectionState(
              state,
              ref.path,
              rawRequest.previous_item_version_id,
            )
          : undefined;

        return mergeDocuments<QuotationItemRevisionsCollection>(
          // If previous outdated item version exists, delete it from the current state.
          previousQuotationItem
            ? removeDocumentById<QuotationItemRevisionsCollection>(
                ref,
                state,
                previousQuotationItem.id,
              )
            : state,
          transformIntoDocuments(
            [result.quotation_item],
            transformQuotationItemRevision,
          ),
          [
            { field: 'order', direction: OrderByDirection.ASC },
            { field: 'id', direction: OrderByDirection.ASC },
          ],
          ref,
        );
      }
      return state;
    }
    case 'API_UPDATE_SUCCESS': {
      if (action.payload.type === 'quotationItemRevisions') {
        const { result } = action.payload;
        const ref: Reference<QuotationItemRevisionsCollection> = {
          path: {
            collection: 'quotationItemRevisions',
            authUid: action.payload.ref.path.authUid,
          },
        };
        return updateWebsocketDocuments<QuotationItemRevisionsCollection>(
          state,
          [result.quotation_item.item_version_id],
          itemRevision => transformQuotationItemRevision(result.quotation_item),
          ref,
        );
      }
      return state;
    }
    case 'API_DELETE_SUCCESS': {
      if (action.payload.type === 'quotationItemRevisions') {
        const { originalDocument, ref } = action.payload;
        return removeDocumentById<QuotationItemRevisionsCollection>(
          ref,
          state,
          originalDocument.id,
        );
      }
      return state;
    }
    case 'WS_MESSAGE': {
      if (
        action.payload.parent_type === 'notifications' &&
        action.payload.type === 'quotationItemsCurrencyUpdate'
      ) {
        const { data } = action.payload;

        return updateWebsocketDocuments<QuotationItemRevisionsCollection>(
          state,
          data.quotation_item_currency_list.map(item => item.item_version_id),
          itemRevision => ({
            ...itemRevision,
            currencyId:
              data.quotation_item_currency_list.find(
                item => item.item_version_id === itemRevision.id,
              )?.currency_id ?? itemRevision.currencyId,
          }),
          {
            path: {
              collection: 'quotationItemRevisions',
              authUid: action.payload.toUserId,
            },
          },
        );
      }
      return state;
    }
    default:
      return state;
  }
}
